import { SectionBreak, Button, H2, HintText, ErrorText, FormGroup, LabelText } from "govuk-react";
import React, { memo, useCallback, useEffect } from "react";
import { useForm } from "../hooks/formContext";
import { GovField } from "@publicMirage/components/govReact/GovField";
import { GovAddressBox, ISelectedAddressRecord } from "@publicMirage/components/govReact/GovAddressBox";
import { getValuesWithNewLines } from "@shared/mirage/utils/getValuesWithNewLines";
import { useBoolean } from "@mirage/hooks/useBoolean";
import { PrePpWlFormComponentStepProps } from "../PrePublicPortalWlForm";
import Box from "@mui/material/Box";

export const PrePpWlSiteDetailsStep = memo((props: PrePpWlFormComponentStepProps) => {
    const { next } = props;
    const { toFieldProps, formState, setFieldValue, previous } = useForm();
    const { proposalAffectsAnyTrees } = formState;

    const siteLocationSameAsApplicantOnChange = useCallback(
        (e: any, value: true | false | null) => {
            setFieldValue("siteLocationIsSameAsApplicant", value);
            if (value) {
                setFieldValue("siteLocationAddress", formState.applicantAddress.value);
                setFieldValue("siteLocationPostTown", formState.applicantPostTown.value);
                setFieldValue("siteLocationPostcode", formState.applicantPostcode.value);
                setFieldValue("siteLocationUprn", formState.applicantUprn.value);
                setFieldValue("siteLocationAddressLine1", formState.applicantAddressLine1.value);
                setFieldValue("siteLocationAddressLine2", formState.applicantAddressLine2.value);
                setFieldValue("siteLocationAddressLine3", formState.applicantAddressLine3.value);
                setFieldValue("siteLocationEasting", formState.applicantEasting.value);
                setFieldValue("siteLocationNorthing", formState.applicantNorthing.value);
            } else {
                setFieldValue("siteLocationAddress", "");
                setFieldValue("siteLocationPostTown", "");
                setFieldValue("siteLocationPostcode", "");
                setFieldValue("siteLocationUprn", "");
                setFieldValue("siteLocationAddressLine1", "");
                setFieldValue("siteLocationAddressLine2", "");
                setFieldValue("siteLocationAddressLine3", "");
                setFieldValue("siteLocationEasting", "");
                setFieldValue("siteLocationNorthing", "");
            }
        },
        [
            formState.applicantAddress.value,
            formState.applicantAddressLine1.value,
            formState.applicantAddressLine2.value,
            formState.applicantAddressLine3.value,
            formState.applicantEasting.value,
            formState.applicantNorthing.value,
            formState.applicantPostTown.value,
            formState.applicantPostcode.value,
            formState.applicantUprn.value,
            setFieldValue,
        ]
    );
    return (
        <>
            <H2>Site Details</H2>
            <SectionBreak level="LARGE" visible />
            <GovField {...toFieldProps("currentUseOfTheSite", "TEXTAREA")} />
            <GovField {...toFieldProps("proposalAffectsAnyTrees", "CHECKBOX")} />
            {proposalAffectsAnyTrees.value ? <HintText>N.B. Please show the trees that will be affected on your Site Location Plan.</HintText> : null}
            <GovField {...toFieldProps("siteLocationIsSameAsApplicant", "CHECKBOX")} onChange={siteLocationSameAsApplicantOnChange} />
            <SiteLocation />

            <Box display="flex" justifyContent="space-between">
                <Button onClick={previous}>Previous</Button>
                <Button onClick={next}>Next</Button>
            </Box>
        </>
    );
});

const SiteLocation = memo(() => {
    const { toFieldProps, setFieldValue, formState } = useForm();
    const { siteLocationAddressLine1, siteLocationAddressLine2, siteLocationAddressLine3, siteLocationPostTown, siteLocationPostcode, siteLocationAddress } = formState;
    const { value: addressEditable, toggle: toggleAddressEditable } = useBoolean(false);
    const updateGazetteerExtraFields = useCallback(
        (easting: string | number, northing: string | number, uprn: string) => {
            setFieldValue("siteLocationEasting", easting);
            setFieldValue("siteLocationNorthing", northing);
            setFieldValue("siteLocationUprn", uprn);
        },
        [setFieldValue]
    );
    const errorMsg = siteLocationAddress.errorMsg;
    const gazeteerOnSelect = useCallback(
        ({ addressDetails, addressLines, point }: ISelectedAddressRecord) => {
            const { easting, northing } = point;
            const { UPRN, TOWN_NAME, POSTCODE } = addressDetails;
            const { line1, line2, line3 } = addressLines;
            const location = getValuesWithNewLines([line1, line2, line3, TOWN_NAME, POSTCODE]);
            if (!addressEditable) {
                updateGazetteerExtraFields(easting, northing, UPRN);
            }
            setFieldValue("siteLocationAddressLine1", line1);
            setFieldValue("siteLocationAddressLine2", line2);
            setFieldValue("siteLocationAddressLine3", line3);
            setFieldValue("siteLocationPostTown", TOWN_NAME);
            setFieldValue("siteLocationPostcode", POSTCODE);
            setFieldValue("siteLocationAddress", location);
        },
        [addressEditable, setFieldValue, updateGazetteerExtraFields]
    );

    useEffect(() => {
        if (addressEditable) {
            updateGazetteerExtraFields("", "", "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteLocationAddressLine1, siteLocationAddressLine2, siteLocationAddressLine3, siteLocationPostTown, siteLocationPostcode]);

    return (
        <>
            <GovAddressBox onSelect={gazeteerOnSelect} />
            <GovField contentType="CHECKBOX" label="I want to enter the address by myself" value={addressEditable} onChange={toggleAddressEditable} />
            <FormGroup error={!!errorMsg} style={{ marginBottom: "0px" }}>
                <LabelText className="required">Address</LabelText>
                <ErrorText>{errorMsg}</ErrorText>
                <GovField {...toFieldProps("siteLocationAddressLine1", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
                <GovField {...toFieldProps("siteLocationAddressLine2", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
                <GovField {...toFieldProps("siteLocationAddressLine3", "TEXT")} label="" errorMsg="" required={false} disabled={!addressEditable} />
            </FormGroup>
            <GovField {...toFieldProps("siteLocationPostTown", "TEXT")} disabled={!addressEditable} />
            <GovField {...toFieldProps("siteLocationPostcode", "TEXT")} disabled={!addressEditable} />
        </>
    );
});
